import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { Link as RouterLink } from 'react-router-dom';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    IconButton,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { API_URL } from '../../api';
import { useConfirm } from '../../components/ConfirmDialog';
import HowItWorksCard from '../../components/HowItWorksCard';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

/**
 * Stand-alone admin page to view, edit and delete the email templates Meera
 * saves from the Email composer. Templates are still created via the
 * composer's "Save as template" panel; this page is the library/edit view.
 */
export default function EmailTemplatesPage() {
    const confirm = useConfirm();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const [editing, setEditing] = useState(null); // template being edited, or null
    const [name, setName] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${API_URL}/api/admin/emails/templates`,
                authHeaders()
            );
            setTemplates(Array.isArray(data) ? data : []);
        } catch {
            setError('Failed to load.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openEdit = (t) => {
        setEditing(t);
        setName(t.name || '');
        setSubject(t.subject || '');
        setBody(t.htmlBody || '');
        setError('');
        setMessage('');
    };

    const closeEdit = () => {
        setEditing(null);
        setName('');
        setSubject('');
        setBody('');
    };

    const save = async () => {
        if (!name.trim() || !subject.trim() || !body.trim()) {
            setError('Name, subject and body are required.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            await axios.patch(
                `${API_URL}/api/admin/emails/templates/${editing._id}`,
                { name: name.trim(), subject: subject.trim(), htmlBody: body },
                authHeaders()
            );
            setMessage('Template updated.');
            closeEdit();
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!(await confirm({ title: 'Delete template', message: 'This cannot be undone.', confirmLabel: 'Delete', danger: true }))) return;
        try {
            await axios.delete(
                `${API_URL}/api/admin/emails/templates/${id}`,
                authHeaders()
            );
            setMessage('Template deleted.');
            await load();
        } catch {
            setError('Failed to delete.');
        }
    };

    const sorted = useMemo(
        () => [...templates].sort((a, b) => String(a.name).localeCompare(String(b.name))),
        [templates]
    );

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4">Email templates</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 640 }}>
                        Re-usable subject + body pairs you can pick from the Email composer's
                        "Load template" dropdown. Create new templates from the composer
                        ("Save as template" panel); edit or delete them here.
                    </Typography>
                    
                </Box>
                <Button
                    component={RouterLink}
                    to="/emails"
                    variant="outlined"
                    sx={{ padding: '8px 22px', fontFamily: '"Inter"', fontWeight: 500 }}
                >
                    Email composer
                </Button>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> Templates are reusable email bodies you can pull into the composer with one click.<br />
                            <strong>2.</strong> Click <strong>New template</strong>, give it a name (e.g. "Trustpilot review request"), set the subject and body.<br />
                            <strong>3.</strong> Edit or delete a template using the row buttons. Deleting doesn't affect emails you've already sent.<br />
                            <strong>4.</strong> Templates are personal to the admin panel — end users never see this list.<br />
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && !editing && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 240 }}>Name</TableCell>
                                <TableCell>Subject</TableCell>
                                <TableCell sx={{ width: 180 }}>Updated</TableCell>
                                <TableCell align="right" sx={{ width: 120 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sorted.map((t) => (
                                <TableRow key={t._id}>
                                    <TableCell>{t.name}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{t.subject || '—'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        {t.updatedAt ? new Date(t.updatedAt).toLocaleString('en-GB') : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => openEdit(t)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton onClick={() => remove(t._id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {sorted.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                            No templates yet. Save one from the Email composer first.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Card>

            <Dialog open={!!editing} onClose={closeEdit} maxWidth="md" fullWidth>
                <DialogTitle>Edit template</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Template name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            size="small"
                        />
                        <TextField
                            label="Subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            size="small"
                        />
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Body
                            </Typography>
                            <ReactQuill theme="snow" value={body} onChange={setBody} />
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEdit} disabled={saving}>Cancel</Button>
                    <Button onClick={save} variant="contained" disabled={saving}>
                        {saving ? 'Saving…' : 'Save changes'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}